import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import {
  SearchForFacetValuesResponse,
  SearchResponse,
  MultipleQueriesResponse,
} from '@algolia/client-search';
import { getPublicHostName } from 'lib/util/urls';
import { logError } from 'lib/util/errors';
import { env } from 'lib/env';
import { FilterCategoryDataLayerObject, gtmDatalayerPush } from './gtm-datalayer';

// Re-export search client and helper so that we use a consolidated version across the stack
export type { SearchClient };
export type { AlgoliaSearchHelper } from 'algoliasearch-helper';

// OK to use any here, as it's what matches Algolia's definitions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare type SearchRequestRecord = Record<string, any>;

/**
 * Default search index for this website and environment.
 * */
export const DEFAULT_SEARCH_INDEX = env.ALGOLIA_INDEX_NAME;

/**
 * Default search index for this website and environment.
 * */
export const REPORTS_SEARCH_INDEX = () => env.ALGOLIA_REPORTS_INDEX_NAME;

/**
 * Default suggestion index for this website and environment.
 * */
export const DEFAULT_SUGGEST_INDEX = env.ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME?.trim()
  ? env.ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME
  : `${env.ALGOLIA_INDEX_NAME}_suggestions`;

/**
 * Empty Algolia response
 * */
export const EMPTY_SEARCH_RESULT = {
  results: [
    {
      hits: [],
      nbHits: 0,
      page: 0,
      nbPages: 0,
      hitsPerPage: 10,
      exhaustiveNbHits: true,
      exhaustiveTypo: true,
      exhaustive: {
        nbHits: true,
        typo: true,
      },
      query: '',
      index: DEFAULT_SEARCH_INDEX,
    },
  ],
};

export const isEmptySearchRequest = (requests: SearchRequestRecord): boolean => {
  // If more than 1 request return false
  if (requests.length > 1) {
    return false;
  }
  const theRequest = requests[0];
  const filterExpression = /^startDate\s>=\s\d+\sOR\sstartDate\s=\s0+\sOR\sendDate\s>=\s\d+$/gm;
  if (
    theRequest?.params && // and has params
    (!theRequest.query || theRequest.query === ``) && // and has a missing or empty query
    (!theRequest.params.query || theRequest.params.query === ``) && // and has a missing or empty params query
    (!theRequest.params.facets || theRequest.params.facets.length === 0) && // and has missing or empty facets
    (!theRequest.params.filters || // and has no filters
      theRequest.params.filters === `` || // or empty filters
      filterExpression.test(theRequest.params.filters)) // or a default startDate filter
  ) {
    return true;
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pushFiltersDataLayerObject = (baseClient: any, value: any, requests?: any) => {
  const request = requests[0];

  if (request?.params?.facetFilters) {
    const facets = request?.params?.facetFilters;

    const gtmData: FilterCategoryDataLayerObject = {
      event: 'e_all_filters',
      contentType: request?.params?.filters,
      resultCount: value?.results[0]?.nbHits,
    };

    if (Array.isArray(facets)) {
      facets.forEach((facet: string[]) => {
        let facetName = '';
        let facetSelected = '';
        if (Array.isArray(facet)) {
          facet.forEach((data, j: number) => {
            const record = data?.split(':');
            if (record.length > 1) {
              if (j === 0) {
                facetName = record[0];
                facetSelected = record[1];
              } else {
                facetSelected = facetSelected?.concat(', ', record[1]);
              }
              gtmData[facetName] = facetSelected;
            }
          });
        }
      });
    }

    const serializedRequest = JSON.stringify(gtmData);
    if (baseClient._myLastFacetFilter !== serializedRequest) {
      // eslint-disable-next-line no-param-reassign
      baseClient._myLastFacetFilter = serializedRequest;
      gtmDatalayerPush(gtmData);
    }
  }
};

// Algolia interface defines return type as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decodeResponseValues = (baseClient: SearchClient, value: any, requests: any) => {
  pushFiltersDataLayerObject(baseClient, value, requests);
  const fromServer = value;
  if (fromServer?.results) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fromServer.results.forEach((result: any, idx: number) => {
      if (result?.query && result?.query !== '') {
        fromServer.results[idx].query = decodeURIComponent(result.query);
        fromServer.results[idx].indexName = decodeURIComponent(result.indexName);
        fromServer.results[idx].highlightPreTag = decodeURIComponent(result.highlightPreTag);
        fromServer.results[idx].highlightPostTag = decodeURIComponent(result.highlightPostTag);
      }
    });
  }
  return fromServer;
};

// Algolia interface defines return type as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decodeFailedResponse = (err: any) => {
  const isAuthFail =
    (err.name === 'DeserializationError' &&
      err.response &&
      /\/auth\/okta/i.test(err.response.content)) ||
    (err.name === 'RetryError' &&
      /Unreachable hosts - your application id may be incorrect/i.test(err.message));
  if (isAuthFail) {
    logError(`User not authenticated - ${err.name}`, err);
    return EMPTY_SEARCH_RESULT;
  }
  logError('Search transport error', err);
  throw err;
};

/**
 * Performs pre and post search processing.
 * @param baseClient The base search client.
 * @param page The search action.
 * @param requests Search Requests to process.
 */
export const searchWithTransporter = (
  baseClient: SearchClient,
  page: string,
  requests: SearchRequestRecord,
  isNeverSearch?: boolean
  // Algolia interface defines return type as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  // If an empty search request is sent
  if (isEmptySearchRequest(requests) || isNeverSearch === true) {
    // Return empty resultset without calling back-end
    const ret = { ...EMPTY_SEARCH_RESULT };
    if (requests[0]?.indexName) {
      ret.results[0].index = requests[0]?.indexName;
    }
    return Promise.resolve(ret);
  }
  const ifReportIndex =
    (REPORTS_SEARCH_INDEX() ?? '') !== '' &&
    `${requests[0]?.indexName}`.startsWith(REPORTS_SEARCH_INDEX() ?? '');
  const searchRoute = ifReportIndex ? `search-reports` : `search`;
  return baseClient.transporter
    .read(
      {
        path: `/api/${searchRoute}/${page}`,
        method: `POST`,
        data: { requests },
        cacheable: true,
      },
      {
        cacheable: true,
        headers: {
          'Content-Type': `application/json`,
        },
      }
    )
    .then((x) => decodeResponseValues(baseClient, x, requests))
    .catch(decodeFailedResponse);
};

const createClientSideInstance = (
  props:
    | {
        baseClient: SearchClient;
        isNeverSearch?: boolean;
      }
    | SearchClient
): SearchClient => {
  let baseClient: SearchClient;
  let isNeverSearch: boolean;

  if (!props) {
    throw new Error(`Invalid search client configuration: no properties provided`);
  }
  if ('baseClient' in props) {
    baseClient = props.baseClient;
    isNeverSearch = props.isNeverSearch === true;
    baseClient.addAlgoliaAgent('NmdpNeverSearch.1.0.0');
  } else {
    baseClient = props;
  }
  if (!baseClient?.transporter) {
    throw new Error('Invalid search client configuration: baseClient is missing or invalid');
  }

  // Algolia defines as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const search = (requests: SearchRequestRecord): Promise<any> =>
    searchWithTransporter(baseClient, `query`, requests, isNeverSearch === true);

  // Algolia defines as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const searchForFacetValues = (requests: SearchRequestRecord): Promise<any> =>
    searchWithTransporter(baseClient, `sffv`, requests, isNeverSearch === true);

  return {
    ...baseClient,
    search,
    searchForFacetValues,
  };
};

const publicHost = getPublicHostName();

/**
 * Creates a new Algolia search client.
 * Initializes and returns a connection to Algolia
 * @returns An initialized search client.
 * */
export const createSearchClient = (forServer = false): SearchClient => {
  return forServer === true
    ? createClientSideInstance({ baseClient: algoliasearch('', ''), isNeverSearch: true })
    : createClientSideInstance(
        algoliasearch('', '', {
          hosts: [publicHost], // Route requests thru vercel to restrict API key access
          timeouts: {
            connect: 2, // Connection timeout in seconds
            read: 10, // Read timeout in seconds
            write: 30, // Write timeout in seconds
          },
        })
      );
};

/**
 * Calculates a report download url given its object id.
 * @param objectId The algolia identifier of the report to download.
 * @returns The path to download the report.
 * */
export const getReportDownloadUrl = (objectId: string): string => {
  const normalizedObjectId = objectId.replaceAll('\\', '/');
  return `/api/search-reports/download/${normalizedObjectId}`;
};

/**
 * Typeguard used to verify we were returned a SearchResponse not a SearchForFacetValuesResponse.
 * @param check the object to evaluate
 * @returns true if the object is of type SearchResponse
 * */
export const isSearchResults = <TObject = {}>(
  check: SearchForFacetValuesResponse | SearchResponse<TObject> | undefined
): check is SearchResponse<TObject> => typeof check !== 'undefined' && 'hits' in check;
/**
 * Typeguard used to verify the result at a given index is a SearchResponse type.
 * @param response The @see MultipleQueriesResponse to evaluate
 * @param index The index of the response to evaluate.  If not set, the first item is used.
 * @returns A @see SearchResponse value, or undefined.
 * */
export const getSearchResultsAt = <TObject = {}>(
  response?: MultipleQueriesResponse<TObject>,
  index?: number
): SearchResponse<TObject> | undefined => {
  // Typeguard against undefined
  if (typeof response === 'undefined') {
    return undefined;
  }
  // Make sure we were passed a valid index
  const theIndex = typeof index === 'undefined' ? 0 : index;
  if (response.results.length <= theIndex) {
    return undefined;
  }
  // Typeguard the value and return it
  const theValueToCheck = response.results[theIndex];
  return isSearchResults(theValueToCheck) ? theValueToCheck : undefined;
};
